import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import SearchIcon from '../../../assets/search.svg'
import { type IDistributionSeries } from '../../../models/DistributionSeries'
import { ProjectActions } from './ProjectActions'
import Search from '../../../components/Search'
import { blue, grey, shade } from '../../../colors'
import { capitalizeFirst } from '../../../utils/utils'
import { ImageWrapper } from '../../../components/CustomTable/NoDataFound/styles'

interface ProjectsSidebarProps {
  projects: IDistributionSeries[]
}

export const ProjectsSidebar: React.FC<ProjectsSidebarProps> = ({ projects }) => {
  const [search, setSearch] = useState('')
  const [hoverAnchor, setHoverAnchor] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const sortedDefaultValue = projects[0]?.distributionSeriesId || ''

  const selectedProjectId = searchParams.get('projectId') || sortedDefaultValue

  const filteredProjects = search.trim()
    ? projects.filter((series) => series?.title?.toLowerCase().includes(search.trim().toLowerCase()))
    : projects

  const handleSidebarRowClick = (_e: React.MouseEvent, projectId: string): void => {
    const params = new URLSearchParams(searchParams)
    params.set('projectId', projectId)
    setSearchParams(params, { replace: true })
  }

  useEffect(() => {
    if (!searchParams.get('projectId') && sortedDefaultValue) {
      const params = new URLSearchParams(searchParams)
      params.set('projectId', sortedDefaultValue)
      setSearchParams(params, { replace: true })
    }
  }, [searchParams, setSearchParams, sortedDefaultValue])

  return (
    <Box sx={{ position: 'relative', height: '100%', p: 1 }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, background: grey[0] }}>
        <Search
          placeholder="Search projects..."
          onChange={setSearch}
          fillContainer
          keepExpanded
          stayFocusOnEnter
        />
      </Box>
      <Box
        pr={3}
        mt={3}
        sx={{
          overflowY: 'auto',
          height: 'calc(100% - 78px)',
          borderRight: `1px solid ${grey[100]}`
        }}
      >
        {filteredProjects.length === 0
          ? (
          <NoProjectsFoundMessage search={search} />
            )
          : (
              filteredProjects.map((series) => {
                const isActive = String(series.distributionSeriesId) === selectedProjectId
                return (
              <Box
                key={series.distributionSeriesId}
                padding={2}
                display='flex'
                alignItems='center'
                justifyContent={'space-between'}
                borderBottom={`1px solid ${grey[0]}`}
                onMouseEnter={() => { setHoverAnchor(series.distributionSeriesId) }}
                onMouseLeave={() => { setHoverAnchor('') }}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '6px',
                  background: isActive ? shade[950] : 'transparent',
                  '&:hover': { background: grey[100] }
                }}
                onClick={e => { handleSidebarRowClick(e, series.distributionSeriesId) }}
                aria-current={isActive ? 'page' : undefined}
                tabIndex={0}
                role="button"
              >
                <Typography
                  title={series.title}
                  color={isActive ? blue[500] : grey[850]}
                  fontSize={14}
                  noWrap
                  sx={{ maxWidth: '96%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {capitalizeFirst(series.title)}
                </Typography>
                {hoverAnchor === series.distributionSeriesId && (
                  <ProjectActions selectedProject={series} />
                )}
              </Box>
                )
              })
            )}
      </Box>
    </Box>
  )
}

interface NoProjectsFoundMessageProps {
  search?: string
}

const NoProjectsFoundMessage: React.FC<NoProjectsFoundMessageProps> = ({ search }) => {
  const hasSearch = Boolean(search?.trim())
  return (
    <Box display={'flex'} flexDirection='column' alignItems='center' justifyContent={'center'} gap={2} height={'60vh'} >
      {hasSearch
        ? <>
          <ImageWrapper>
            <img src={SearchIcon} alt="Search Icon" />
          </ImageWrapper>
          <Box>
            <Typography fontWeight='bold' textAlign='center'>No results</Typography>
            <Typography textAlign='center' color={grey[650]} fontSize='14px' lineHeight={2}>
              Please try again.
            </Typography>
          </Box>
        </>
        : <Typography color={grey[500]} fontWeight="bold" fontSize={16}>
          No survey projects available.<br />
          Please create a new project to get started.
        </Typography>
      }
    </Box>
  )
}
